import React, { useEffect } from 'react';
import ClarificationPanel from './ClarificationPanel.mjs';

/**
 * The questions about one claim or report, in a panel at the side of the
 * screen - so a reviewer can ask, and read the answer, without leaving the
 * claim-by-claim walkthrough or the report they are on.
 *
 * Sits above the walkthrough (z 9000) and its reason box (9100); the
 * attachment viewer inside it (9400) and the app's dialogs (10500) sit above.
 * Escape closes the drawer only - the walkthrough's own key handler must
 * ignore keys while this is open, or typing would page its documents.
 */
const ClarificationDrawer = ({ title, subtitle, claimType, claimId, reportId, canAsk = true, onClose, onChange }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 z-[9150] bg-black/30" onMouseDown={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Questions'}
        className="fixed inset-y-0 right-0 z-[9200] w-full sm:w-[480px] bg-white shadow-2xl flex flex-col"
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{title || 'Questions'}</h2>
            {subtitle && <p className="text-xs text-gray-500 break-words">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close questions"
            className="w-11 h-11 -mr-2 -mt-1 flex items-center justify-center text-gray-400 hover:text-gray-700 text-2xl leading-none"
          >
            &times;
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <ClarificationPanel
            claimType={claimType}
            claimId={claimId}
            reportId={reportId}
            canAsk={canAsk}
            onChange={onChange}
          />
        </div>
      </aside>
    </>
  );
};

export default ClarificationDrawer;
